import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';

import { BulkCreateCadenceBoundary } from '../features/create/bulk-create-controller.js';
import { QuickCreateBoundary } from '../features/create/quick-create.js';
import { GenerationLifecycleBoundary } from '../features/generations/generation-lifecycle.js';
import { ExpandedPlayer } from '../player/expanded-player.js';
import { AppErrorBoundary } from './app-error-boundary.js';
import { SessionBoundary, WorkspaceBoundary } from './app-boundaries.js';
import { AppShell } from './app-shell.js';
import type { SessionResource, WorkspaceResource } from './runtime.js';

export type AppRouteElements = {
  login: ReactNode;
  create: ReactNode;
  bulkCreate: ReactNode;
  projects: ReactNode;
  project: ReactNode;
  openingImpactAudit: ReactNode;
  kk7BoundaryAudit: ReactNode;
  onsetAudit: ReactNode;
  generation: ReactNode;
  catalog: ReactNode;
  library: ReactNode;
  track: ReactNode;
  playlists: ReactNode;
  playlist: ReactNode;
  activity: ReactNode;
  settings: ReactNode;
  delivery: ReactNode | undefined;
};

export function AppRoutes({
  session,
  workspace,
  routes,
  onSignOut,
}: {
  session: SessionResource;
  workspace: WorkspaceResource;
  routes: AppRouteElements;
  onSignOut: () => Promise<void>;
}) {
  return (
    <AppErrorBoundary onReset={() => window.location.reload()}>
      <Routes>
        <Route path="/login" element={routes.login} />
        <Route element={<SessionBoundary resource={session} />}>
          <Route element={<WorkspaceBoundary resource={workspace} />}>
            <Route element={<GenerationLifecycleBoundary />}>
              <Route element={<QuickCreateBoundary />}>
                <Route element={<BulkCreateCadenceBoundary />}>
                  <Route path="/now-playing" element={<ExpandedPlayer />} />
                  <Route
                    element={
                      session.status === 'authenticated' ? (
                        <AppShell session={session.session} onSignOut={onSignOut} />
                      ) : null
                    }
                  >
                    <Route index element={<Navigate to="/create" replace />} />
                    <Route path="/create" element={routes.create} />
                    <Route path="/bulk-create" element={routes.bulkCreate} />
                    <Route path="/projects" element={routes.projects} />
                    <Route path="/projects/:id" element={routes.project} />
                    <Route path="/audits/opening-impact" element={routes.openingImpactAudit} />
                    <Route
                      path="/audits/opening-impact/:projectId"
                      element={routes.openingImpactAudit}
                    />
                    <Route path="/audit/onset/kk11" element={routes.onsetAudit} />
                    <Route path="/audit/onset/kk7" element={routes.kk7BoundaryAudit} />
                    <Route path="/generations/:id" element={routes.generation} />
                    <Route path="/catalog" element={routes.catalog} />
                    <Route path="/library" element={routes.library} />
                    <Route path="/tracks/:id" element={routes.track} />
                    <Route path="/playlists" element={routes.playlists} />
                    <Route path="/playlists/:id" element={routes.playlist} />
                    <Route path="/activity" element={routes.activity} />
                    <Route path="/settings" element={routes.settings} />
                    {routes.delivery ? (
                      <Route path="/deliveries/:id" element={routes.delivery} />
                    ) : null}
                    <Route path="*" element={<Navigate to="/create" replace />} />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
      </Routes>
    </AppErrorBoundary>
  );
}
